/**
 * Step 2B: 画像アップロードステップ
 */
import { useRef } from "react";
import { usePickImageAsset } from "app/hook";
import { usePlaySound } from "common/utils";
import { UserAvatar } from "common/elements";
import I18n from "features/parent/i18n";
import { EditorState } from "features/parent/components/PlayerIconEditor/types";

interface UploadIconStepProps {
  state: EditorState;
  updateState: (updates: Partial<EditorState>) => void;
  targetUid: string;
}

export const UploadIconStep = ({ state, updateState, targetUid }: UploadIconStepProps) => {
  const play = usePlaySound();
  const pickImageAsset = usePickImageAsset();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectFile = () => {
    play();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      return;
    }

    // 即座にS3にアップロード
    pickImageAsset(
      file,
      targetUid,
      (asset) => {
        updateState({ icon: asset.url });
      },
      () => {
        // エラー時はinputをリセット
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      },
    );
  };

  const _handleRemoveIcon = () => {
    play();
    updateState({ icon: "" });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex-col-el flex-center w-full h-full">
      <div className="flex-col-el flex-center mb-8">
        <p className="text-body text-textcolor-black text-[32px]">
          {I18n.t("MSG_UPLOAD_ICON_TITLE_BY_PARENT")}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="flex-col-el flex-center">
        {state.icon ? (
          <div className="relative cursor-pointer group" onClick={handleSelectFile}>
            <UserAvatar size={200} icon={state.icon} />
            <div className="absolute bottom-0 right-0 w-16 h-16 rounded-full bg-gray2-100 flex items-center justify-center shadow-md">
              <span className="material-symbols-outlined text-[54px] text-white">photo_camera</span>
            </div>
          </div>
        ) : (
          <div
            onClick={handleSelectFile}
            className="flex-col-el flex-center w-[400px] h-[250px] border-2 border-dashed rounded-[12px] cursor-pointer transition-all border-gray2-60 hover:border-blue-100"
          >
            <span className="material-symbols-outlined !text-[64px] text-gray2-60 mb-4">
              cloud_upload
            </span>
            <p className="text-[18px] text-textcolor-black mb-2">
              {I18n.t("MSG_UPLOAD_ICON_DRAG_DROP_BY_PARENT")}
            </p>
            <p className="text-[14px] text-gray2-80">{I18n.t("MSG_UPLOAD_ICON_CLICK_BY_PARENT")}</p>
          </div>
        )}
      </div>

      {/* ファイルサイズ・形式の注意書き */}
      <p className="text-[14px] text-gray2-80 mt-4">{I18n.t("MSG_UPLOAD_ICON_HINT_BY_PARENT")}</p>
    </div>
  );
};
